/**
 * «Финансы → Правила»: по каким признакам операция получает статью. Первое
 * совпавшее по приоритету выигрывает. Заводят правило с экрана «Операции»
 * (кнопка у операции) — там видно, на чём оно сработает.
 */
import { RefreshCw, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";

import { NoAccess } from "@/components/NoAccess";
import { PNL, PNL_EDIT, useCan } from "@/lib/access";
import { archiveRule, loadRules, reclassify, type Rule } from "@/lib/pnl";

const TH = "border-b border-border px-2.5 py-2 text-left";
const TD = "border-b border-border px-2.5 py-1.5 align-top";

export function RulesPage() {
  return useCan(PNL) ? <RulesScreen /> : <NoAccess what="правилам разнесения" />;
}

function RulesScreen() {
  const canEdit = useCan(PNL_EDIT);
  const [rules, setRules] = useState<Rule[] | null>(null);
  const [failure, setFailure] = useState("");
  const [notice, setNotice] = useState("");

  const reload = useCallback(() => {
    loadRules()
      .then(setRules)
      .catch((error: Error) => setFailure(error.message));
  }, []);
  useEffect(reload, [reload]);

  const remove = (rule: Rule) => {
    if (!window.confirm(`Выключить правило №${rule.id} → «${rule.article_name}»? Его операции переразнесутся.`)) return;
    setFailure("");
    archiveRule(rule.id)
      .then((r) => {
        setNotice(`Правило выключено, переразнесено операций: ${r.changed}`);
        reload();
      })
      .catch((error: Error) => setFailure(error.message));
  };

  const again = () => {
    setFailure("");
    reclassify()
      .then((r) => {
        setNotice(`Переразнесено операций: ${r.changed}`);
        reload();
      })
      .catch((error: Error) => setFailure(error.message));
  };

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4 [&>*]:flex-none">
      <div className="flex items-center gap-3 text-[12.5px] text-fg-muted">
        <span>
          Свои переводы (контрагент — наш ИП или наш счёт) узнаются раньше правил. Новое правило заводится на экране{" "}
          <Link to="/pnl/operations" className="text-accent-text hover:underline">
            «Операции»
          </Link>
          .
        </span>
        {canEdit ? (
          <button type="button" onClick={again} className="ml-auto flex items-center gap-1 text-accent-text hover:underline">
            <RefreshCw size={12} />
            переразнести всё
          </button>
        ) : null}
      </div>
      {failure ? (
        <div role="alert" className="rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-3 py-2 text-[12.5px] text-err">
          {failure}
        </div>
      ) : null}
      {notice ? <div className="text-[12.5px] text-ok">{notice}</div> : null}

      {rules === null ? (
        <div className="text-[12.5px] text-fg-muted">…</div>
      ) : (
        <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
          <table className="w-full border-collapse text-[12.5px]">
            <thead>
              <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
                <th className={TH}>приоритет</th>
                <th className={TH}>направление</th>
                <th className={TH}>условия</th>
                <th className={TH}>статья</th>
                <th className={`${TH} text-right`}>операций</th>
                <th className={`${TH} w-[1%]`} />
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <tr key={rule.id} data-annot={`правило ${rule.id}`} className="hover:bg-bg-hover">
                  <td className={`${TD} tabular-nums text-fg-muted`}>{rule.priority}</td>
                  <td className={`${TD} text-fg-muted`}>
                    {rule.direction === "in" ? "приход" : rule.direction === "out" ? "расход" : "любое"}
                  </td>
                  <td className={TD}>
                    <Conditions rule={rule} />
                  </td>
                  <td className={TD}>{rule.article_name}</td>
                  <td className={`${TD} text-right tabular-nums text-fg-muted`}>{rule.operations}</td>
                  <td className={`${TD} text-right`}>
                    {canEdit ? (
                      <button
                        type="button"
                        title="Выключить"
                        onClick={() => remove(rule)}
                        className="rounded p-1 text-fg-faint hover:bg-bg-hover hover:text-err"
                      >
                        <Trash2 size={13} />
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}

function Conditions({ rule }: { rule: Rule }) {
  const parts: [string, string][] = [];
  if (rule.counterparty_inn) parts.push(["ИНН", rule.counterparty_inn]);
  if (rule.account_prefix) parts.push(["счёт на", rule.account_prefix]);
  if (rule.counterparty_pattern) parts.push(["контрагент ~", rule.counterparty_pattern]);
  if (rule.purpose_pattern) parts.push(["назначение ~", rule.purpose_pattern]);
  return (
    <div className="grid gap-0.5">
      {parts.map(([label, value]) => (
        <div key={label}>
          <span className="text-fg-faint">{label} </span>
          <code className="font-mono text-[11.5px]">{value}</code>
        </div>
      ))}
    </div>
  );
}
